import type {
  ConnectionQuality,
  QualityStats,
} from '@/hooks/use-connection-quality';

const qualityConfig: Record<
  NonNullable<ConnectionQuality>,
  { bars: number; color: string; label: string }
> = {
  good: { bars: 3, color: 'bg-green-400', label: 'Good' },
  poor: { bars: 2, color: 'bg-yellow-400', label: 'Poor' },
  bad: { bars: 1, color: 'bg-red-400', label: 'Bad' },
};

function buildTooltip(stats: QualityStats): string {
  const { quality, packetLossPercent, roundTripTimeMs, video } = stats;
  if (!quality) return '';

  const lines: string[] = [`Connection: ${qualityConfig[quality].label}`];

  if (video.width && video.height) {
    lines.push(`Resolution: ${video.width}x${video.height}`);
  }
  if (video.framesPerSecond != null) {
    lines.push(`Frame rate: ${video.framesPerSecond} fps`);
  }
  if (video.bitrateKbps != null) {
    lines.push(`Bitrate: ${video.bitrateKbps} kbps`);
  }
  if (packetLossPercent != null) {
    lines.push(`Packet loss: ${packetLossPercent}%`);
  }
  if (roundTripTimeMs != null) {
    lines.push(`Latency: ${roundTripTimeMs} ms`);
  }
  if (video.framesDroppedPercent != null && video.framesDroppedPercent > 0) {
    lines.push(`Frames dropped: ${video.framesDroppedPercent}%`);
  }

  return lines.join('\n');
}

export default function ConnectionQualityIndicator({
  stats,
}: {
  stats: QualityStats;
}) {
  if (!stats.quality) return null;

  const { bars, color } = qualityConfig[stats.quality];

  return (
    <div className="flex items-end gap-0.5" title={buildTooltip(stats)}>
      {[1, 2, 3].map((bar) => (
        <div
          className={`w-1 rounded-sm ${bar <= bars ? color : 'bg-white/30'}`}
          key={bar}
          style={{ height: `${bar * 4 + 4}px` }}
        />
      ))}
    </div>
  );
}
